/**
 * SubGroupSection Component
 * Displays a sub-group header with tasks within a phase accordion
 * MED-160: Added cohort status badge next to sub-group name
 * MED-479: Pass-through subtask expansion props
 */

import { Badge, Box, Group, Stack, Text } from '@mantine/core';
import type { TaskWithDetails, TaskStatus, CohortStatus } from '../../../../types';
import { COHORT_STATUS_CONFIG } from '../../../../config/statusConfig';
import { TaskListRow } from './TaskListRow';

interface SubGroupSectionProps {
  name: string;
  tasks: TaskWithDetails[];
  cohortStatus?: CohortStatus | null; // MED-160: Sub-group's mapped cohort status
  onToggleComplete: (taskId: string, currentStatus: TaskStatus) => void;
  onTaskClick: (task: TaskWithDetails) => void;
  onEditTask?: (task: TaskWithDetails) => void;
  onDeleteTask?: (taskId: string) => void;
  onDuplicateTask?: (taskId: string) => void;
  // MED-479: Subtask expansion props
  expandedTaskIds?: Set<string>;
  loadingTaskIds?: Set<string>;
  subtaskCache?: Record<string, TaskWithDetails[]>;
  onToggleExpand?: (taskId: string) => void;
  /** MED-770: Cohort ID for generating copy-link URLs */
  cohortId?: string;
}

export function SubGroupSection({
  name,
  tasks,
  cohortStatus,
  onToggleComplete,
  onTaskClick,
  onEditTask,
  onDeleteTask,
  onDuplicateTask,
  expandedTaskIds,
  loadingTaskIds,
  subtaskCache,
  onToggleExpand,
  cohortId,
}: SubGroupSectionProps) {
  return (
    <Stack gap={0}>
      {/* Sub-group Header with optional status badge - MED-162 styling */}
      <Box
        style={{
          borderBottom: '1px solid #F0F0F0',
          backgroundColor: '#FAFAFA',
        }}
      >
        <Group
          justify="space-between"
          px="lg"
          pt="md"
          pb="xs"
        >
          <Text
            size="xs"
            fw={600}
            tt="uppercase"
            style={{ letterSpacing: '0.3px', color: '#888888' }}
          >
            {name}
          </Text>
          {cohortStatus && (
            <Badge
              size="xs"
              color={COHORT_STATUS_CONFIG[cohortStatus]?.color || 'gray'}
              variant={COHORT_STATUS_CONFIG[cohortStatus]?.variant || 'light'}
            >
              {COHORT_STATUS_CONFIG[cohortStatus]?.label || cohortStatus}
            </Badge>
          )}
        </Group>
      </Box>

      {/* Task List */}
      <Stack gap={0}>
        {tasks.map((task) => (
          <TaskListRow
            key={task.id}
            task={task}
            onToggleComplete={onToggleComplete}
            onClick={onTaskClick}
            onEdit={onEditTask}
            onDelete={onDeleteTask}
            onDuplicate={onDuplicateTask}
            isExpanded={expandedTaskIds?.has(task.id)}
            isLoadingSubtasks={loadingTaskIds?.has(task.id)}
            onToggleExpand={onToggleExpand}
            subtasks={subtaskCache?.[task.id]}
            cohortId={cohortId}
          />
        ))}
      </Stack>
    </Stack>
  );
}

export default SubGroupSection;
